import React from 'react'
import { Text, View } from 'react-native'
import {
  BG_STYLES,
  BUTTON_STATUS,
  BottomDrawer,
  Button,
  BUTTON_TYPE,
  CHIP_STATES,
  Chip,
  COLORS,
  COMMON_STYLES,
  CrossIcon,
  DeleteIcon,
  LazyImage,
  SIZE,
  TEXT_STYLES,
} from '@dehaat/dds'
import ApproveIcon from 'app/icons/Approve'
import {
  ReviewIssueCropIcon,
  ReviewIssueLocationIcon,
  ReviewIssuePhoneIcon,
  ReviewIssueUserIcon,
} from 'app/icons/ReviewIssueIcons'
import { RequestAction, RoleType, STATUS } from 'app/constants/request'
import { DemoMaterialRequest } from 'app/models/RequestFlow'
import { useTranslation } from 'react-i18next'
import DemoMaterialTileImage from 'app/components/requestType/DemoMaterialTileImage'
import SalesOrderChip from './SalesOrderChip'
import DemoDetailApproveConfirmModal from './DemoDetailApproveConfirmModal'

type Props = {
  visible: boolean
  selectedRequest: DemoMaterialRequest
  roleType?: string
  styles: any
  actionLoading: boolean
  isDetailAction: boolean
  showConfirmModal: boolean
  pendingAction: RequestAction | null
  canTakeApproverAction: (item: DemoMaterialRequest) => boolean
  canWithdrawRequest: (item: DemoMaterialRequest) => boolean
  getDisplayStatus: (latestStatus?: string) => string
  getStatusPillStyle: (latestStatus?: string) => any
  getStatusTextStyle: (latestStatus?: string) => any
  getPartnerId: (item: DemoMaterialRequest) => string
  getApproverName: (item: DemoMaterialRequest, level: RoleType) => string
  getApproverStatusText: (item: DemoMaterialRequest, level: RoleType) => string
  getRejectionReason: (item: DemoMaterialRequest) => string
  renderInfoRow: (label: string, value: string, numberOfLines?: number) => React.ReactNode
  onClose: () => void
  onOpenReject: () => void
  onTriggerApprove: () => void
  onSetSelectedRequestIds: (ids: number[]) => void
  onWithdrawRequest: (requestId: number) => void
  onCloseConfirm: () => void
  onConfirmApprove: () => void
}

const DemoMaterialRequestDetailSheet = ({
  visible,
  selectedRequest,
  roleType,
  styles,
  actionLoading,
  isDetailAction,
  showConfirmModal,
  pendingAction,
  canTakeApproverAction,
  canWithdrawRequest,
  getDisplayStatus,
  getStatusPillStyle,
  getStatusTextStyle,
  getPartnerId,
  getApproverName,
  getApproverStatusText,
  getRejectionReason,
  renderInfoRow,
  onClose,
  onOpenReject,
  onTriggerApprove,
  onSetSelectedRequestIds,
  onWithdrawRequest,
  onCloseConfirm,
  onConfirmApprove,
}: Props) => {
  const { t } = useTranslation()
  const latestStatus = (selectedRequest.latest_status || '').toUpperCase()
  const isApprovedRequest =
    latestStatus.includes('APPROVED') ||
    (roleType === RoleType.L1 &&
      [STATUS.L2_PENDING, STATUS.L2_APPROVED, STATUS.L2_REJECTED].includes(latestStatus))
  const getApproverStatusStyle = (level: RoleType) => {
    const status = (
      selectedRequest.approvers?.find(approver => approver.level === level)?.status || ''
    ).toUpperCase()
    if (status.includes('APPROVED')) return styles.approverStatusApproved
    if (status.includes('REJECTED')) return styles.approverStatusRejected
    if (status.includes('PENDING')) return styles.approverStatusPending
    return styles.approverStatusNeutral
  }

  return (
    <BottomDrawer visible={visible} closeOnBackdropPress={!actionLoading} onClose={onClose}>
      <View style={styles.detailSheetWrap}>
        <View style={styles.detailTopRow}>
          <View style={styles.detailProductRow}>
            <LazyImage
              imageUrl={selectedRequest.product_variant?.product_image}
              imgStyles={styles.detailProductImage}
              fallBackIcon={<DemoMaterialTileImage size={56} />}
            />
            <View style={COMMON_STYLES.flex1}>
              <Text style={styles.detailBrandText}>
                {selectedRequest.product_variant?.brand || t('demo_material')}
              </Text>
              <Text style={styles.detailProductName}>
                {selectedRequest.product_variant?.product_name || '-'}
              </Text>
              <Text style={styles.detailVariantText}>
                {selectedRequest.product_variant?.attributes_name
                  ? `${selectedRequest.product_variant.attributes_name} x ${selectedRequest.units || 0}`
                  : `- x ${selectedRequest.units || 0}`}
              </Text>
            </View>
          </View>
          <View style={styles.detailMetaCol}>
            <View style={styles.detailTagRow}>
              <Chip
                label={t('demo_material_issue')}
                state={CHIP_STATES.INACTIVE}
                containerStyle={styles.requestTypeChip}
                labelStyle={styles.requestTypeChipLabel}
              />
            </View>
            <View style={styles.statusRow}>
              <Text style={styles.statusLabel}>{t('status_label')}</Text>
              <Chip
                label={getDisplayStatus(selectedRequest.latest_status)}
                state={CHIP_STATES.ACTIVE}
                activeContainerStyle={[
                  styles.statusChip,
                  getStatusPillStyle(selectedRequest.latest_status),
                ]}
                labelStyle={[
                  styles.statusChipLabel,
                  getStatusTextStyle(selectedRequest.latest_status),
                ]}
              />
            </View>
          </View>
        </View>

        <Text style={styles.issueForTitle}>{t('issue_for')}</Text>
        <View style={styles.detailDivider} />
        <View style={styles.issueRow}>
          <View style={styles.issueItem}>
            <View style={styles.issueIconSlot}>
              <ReviewIssueUserIcon />
            </View>
            <Text style={styles.issueText}>{selectedRequest.farmer_name || '-'}</Text>
          </View>
          <View style={styles.issueMiniDivider} />
          <View style={styles.issueItem}>
            <View style={styles.issueIconSlot}>
              <ReviewIssuePhoneIcon />
            </View>
            <Text style={styles.issueText}>{selectedRequest.farmer_mobile || '-'}</Text>
          </View>
        </View>
        <View style={styles.issueItem}>
          <View style={styles.issueIconSlot}>
            <ReviewIssueLocationIcon />
          </View>
          <Text style={styles.issueText}>
            {[
              selectedRequest.address,
              selectedRequest.block?.name,
              selectedRequest.district?.name,
              selectedRequest.state?.name,
              selectedRequest.pin_code,
            ]
              .filter(Boolean)
              .join(', ') || '-'}
          </Text>
        </View>
        <View style={styles.issueItem}>
          <View style={styles.issueIconSlot}>
            <ReviewIssueCropIcon />
          </View>
          <Text style={styles.issueText}>{selectedRequest.crop?.name || '-'}</Text>
        </View>

        <View style={styles.detailDivider} />
        {isApprovedRequest ? (
          <View style={styles.saleOrderDetailWrap}>
            <SalesOrderChip request={selectedRequest} />
          </View>
        ) : null}
        {renderInfoRow(t('demo_request_id_label'), String(selectedRequest.id))}
        {renderInfoRow(t('demo_requested_by_label'), selectedRequest.requested_by?.full_name || '-')}
        {renderInfoRow(t('demo_partner_name_label'), selectedRequest.vendor?.company_name || '-')}
        {renderInfoRow(
          t('demo_partner_location_label'),
          [selectedRequest.vendor?.district?.name, selectedRequest.vendor?.state?.name]
            .filter(Boolean)
            .join(', ') || '-',
        )}
        {renderInfoRow(t('demo_partner_id_label'), getPartnerId(selectedRequest))}
        <View style={styles.approverInfoRow}>
          <Text style={styles.infoLabel}>{t('demo_l1_approver_label')}</Text>
          <Text style={styles.infoValue}>{getApproverName(selectedRequest, RoleType.L1)}</Text>
          <Text style={[styles.approverStatusText, getApproverStatusStyle(RoleType.L1)]}>
            {getApproverStatusText(selectedRequest, RoleType.L1)}
          </Text>
        </View>
        <View style={styles.approverInfoRow}>
          <Text style={styles.infoLabel}>{t('demo_l2_approver_label')}</Text>
          <Text style={styles.infoValue}>{getApproverName(selectedRequest, RoleType.L2)}</Text>
          <Text style={[styles.approverStatusText, getApproverStatusStyle(RoleType.L2)]}>
            {getApproverStatusText(selectedRequest, RoleType.L2)}
          </Text>
        </View>
        {renderInfoRow(t('demo_rejection_reason_label'), getRejectionReason(selectedRequest), 3)}

        {canTakeApproverAction(selectedRequest) && (
          <View style={[COMMON_STYLES.row, COMMON_STYLES.mt1, { gap: 12 }]}>
            <Button
              buttonStyle={[BG_STYLES.bgError10, styles.detailActionBtn]}
              onClick={onOpenReject}
              type={BUTTON_TYPE.PRIMARY}
              text={t('reject')}
              size={SIZE.small}
              status={BUTTON_STATUS.ACTIVE}
              textStyle={[TEXT_STYLES.textError100]}
              iconLeft={<CrossIcon height={16} width={16} strokeColor="error100" />}
            />
            <Button
              buttonStyle={[BG_STYLES.bgPrimary10, styles.detailActionBtn]}
              onClick={() => {
                onSetSelectedRequestIds([selectedRequest.id])
                onTriggerApprove()
              }}
              type={BUTTON_TYPE.PRIMARY}
              text={t('approve')}
              size={SIZE.small}
              status={BUTTON_STATUS.ACTIVE}
              textStyle={[TEXT_STYLES.textPrimary100]}
              iconLeft={<ApproveIcon width={16} height={16} color={COLORS.primary100} />}
            />
          </View>
        )}
        {!canTakeApproverAction(selectedRequest) && canWithdrawRequest(selectedRequest) && (
          <Button
            buttonStyle={[BG_STYLES.bgError10, styles.detailActionBtn]}
            onClick={() => {
              if (actionLoading) return
              onWithdrawRequest(selectedRequest.id)
            }}
            type={BUTTON_TYPE.PRIMARY}
            text={actionLoading ? t('demo_please_wait') : t('demo_withdraw_request')}
            size={SIZE.small}
            status={actionLoading ? BUTTON_STATUS.DISABLED : BUTTON_STATUS.ACTIVE}
            textStyle={[TEXT_STYLES.textError100]}
            iconLeft={
              <DeleteIcon
                svgProps={styles.image16x16}
                pathProps={{ stroke: COLORS.error100 }}
              />
            }
          />
        )}
        {isDetailAction && showConfirmModal && pendingAction === RequestAction.APPROVE && (
          <DemoDetailApproveConfirmModal
            styles={styles}
            isVisible={showConfirmModal}
            actionLoading={actionLoading}
            onClose={onCloseConfirm}
            onConfirm={onConfirmApprove}
          />
        )}
      </View>
    </BottomDrawer>
  )
}

export default DemoMaterialRequestDetailSheet
